/**
 * Platform feature-flag codes (mirror backend `FeatureFlagCodes`). Kept as named
 * constants so gating never relies on magic strings.
 *
 * Only campaigns are flagged here. The software features are sold as one subscription, so
 * "can this venue use loyalty / promotions / reports / tournaments" is the single question
 * `VenueAccessStore.isLive()` answers — not a flag each. Campaigns stay flagged because they are
 * metered and billed separately from the subscription.
 */
export const FeatureFlagCodes = {
  Campaigns: 'CAMPAIGNS',
  CampaignsFree: 'CAMPAIGNS_FREE',
  /**
   * Events with a venue-built registration form. Flagged per venue rather than sold with the
   * subscription because it ships dark: the player app only grows an events surface in a build
   * that is not in the stores yet, and a venue that could publish an event nobody can see would
   * be collecting registrations into a void.
   */
  Events: 'EVENTS',
  /**
   * Selling amenities as booking add-ons. Off = amenities are informational, so the
   * whole pricing surface (prices, sport scope, the paid checkbox) is hidden rather
   * than shown inert: the server only charges for them while this is on, and a price
   * a venue can set but nobody can be charged is a promise the app does not keep.
   */
  PaidAmenities: 'PAID_AMENITIES',
  /**
   * The venue bears the platform and processing fees refunded to participants when it cancels
   * an event; recovered off its next payments. Read here only to SAY so — in the cancel dialog
   * and on the Facturare card — never to decide anything: the server raises the charge or not.
   */
  VenueCancellationFees: 'VENUE_CANCELLATION_FEES',
  /**
   * The venue's coaches. Per-venue opt-in, seeded dark: while it is off the event form does not
   * offer a coach, because there is no coach catalog for the venue to pick from.
   */
  Coaching: 'COACHING',
  /**
   * Percentage discount codes a player types at checkout. Per-venue opt-in and seeded off: a
   * venue that has not asked for codes should not be shown a screen whose codes the server
   * would refuse, and the mobile app only offers the input once the venue has one live.
   */
  DiscountCodes: 'DISCOUNT_CODES',
  /**
   * Booking without paying by card, settling at the desk instead. Off by default because it is
   * being trialled: the platform collects no fee on these, so a venue only gets the option
   * once it has agreed to invoice the commission separately.
   */
  PayAtVenue: 'PAY_AT_VENUE',
} as const;

/**
 * Codes decided once for the whole platform, with no per-venue half.
 *
 * Most flags answer "may THIS venue use it", and the backend enforces the venue's own row.
 * These answer "does the product exist yet", so every venue gets the same answer and no
 * per-complex row is ever written — the server gates them on the global switch alone.
 * `FeatureFlagStore` therefore reads them from the platform list rather than the venue's,
 * where they would always be absent and so always read as off.
 *
 * Adding a code here without the matching server-side change would unlock a surface the API
 * still refuses, so the two move together.
 */
export const PlatformWideFlagCodes: ReadonlySet<string> = new Set<string>([
  FeatureFlagCodes.PaidAmenities,
]);

/*
 * There is no EVENTS_ACCESS_FLAGS list any more. It paired EVENTS with the older TOURNAMENTS flag
 * so that a venue holding only the older one did not lose its tournaments when they moved under
 * /events. TOURNAMENTS has since been retired as a per-venue flag — it survives only as a
 * platform-wide publish switch for player apps already installed — and the protection it provided
 * now comes from the backend, where EVENTS is granted by default to any venue without an explicit
 * row. So the events screens gate on `FeatureFlagCodes.Events` alone.
 */
